import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useParams } from "react-router-dom";
import { Article, articles, parsedDate } from "@/lib/utils";
import {
  ArrowLeft,
  Calendar,
  Share2,
  Facebook,
  Linkedin,
  Mail,
} from "lucide-react";
import { Link } from "react-router-dom";

const ArticleDetails = () => {
  const { slug } = useParams();
  //   console.log(slug);
  const [article, setArticle] = useState<Article | null>(null);
  const [relatedArticles, setRelatedArticles] = useState<Article[]>([]);
  const [isShareOpen, setIsShareOpen] = useState(false);

  useEffect(() => {
    const foundArticle = articles.find((a) => a.slug === slug);
    if (foundArticle) {
      setArticle(foundArticle);
      // Get related articles (excluding current article)
      const related = articles
        .filter((a) => a.id !== foundArticle.id)
        .slice(0, 3);
      setRelatedArticles(related);
    }
  }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-800">
            Article non trouvé
          </h2>
          <Link
            to="/actualites"
            className="mt-4 inline-flex items-center text-[#1E3D1A] hover:text-[#1E3D1A]/80"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour aux articles
          </Link>
        </div>
      </div>
    );
  }

  const shareUrl = window.location.href;
  const shareText = article.title;

  // Split description into paragraphs
  const paragraphs = article.description
    .split("\n")
    .filter((para) => para.trim());

  return (
    <div className="min-h-screen bg-gradient-to-b w-full from-gray-50 to-white">
      {/* Hero Section */}
      <section className="relative">
        {/* <div className="absolute inset-0 bg-black/30" /> */}
        <img
          src={article.image}
          alt={article.title}
          className="w-full h-[90vh] object-cover object-center md:object-left-top z-50"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative py-16 flex flex-col justify-end">
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <div className="flex items-center gap-4 text-white/90 mb-6">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                <span>{parsedDate(article.date)}</span>
              </div>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
              {article.title}
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="max-w-4xl mx-auto">
          {/* Share Button */}
          <div className="flex items-center gap-4 justify-between mb-12">
            <button
              onClick={() => setIsShareOpen(!isShareOpen)}
              className="flex items-center gap-2 text-[#1E3D1A] hover:text-[#1E3D1A]/80 transition-colors duration-300"
            >
              <Share2 className="w-5 h-5" />
              <span>Partager</span>
            </button>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className="flex items-center gap-4"
            >
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                  shareUrl
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-full bg-blue-600 text-white hover:bg-blue-700 transition-colors duration-300"
              >
                <Facebook className="w-4 h-4" />
              </a>
              <a
                href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(
                  shareUrl
                )}&title=${encodeURIComponent(shareText)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-full bg-blue-700 text-white hover:bg-blue-800 transition-colors duration-300"
              >
                <Linkedin className="w-4 h-4" />
              </a>
              <a
                href={`mailto:?subject=${encodeURIComponent(
                  shareText
                )}&body=${encodeURIComponent(shareUrl)}`}
                className="p-2 rounded-full bg-gray-600 text-white hover:bg-gray-700 transition-colors duration-300"
              >
                <Mail className="w-4 h-4" />
              </a>
            </motion.div>
          </div>

          {/* Article Content */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="prose prose-lg max-w-none"
          >
            {paragraphs.map((paragraph, index) => (
              <motion.p
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
                className="text-gray-700 leading-relaxed mb-6 text-lg"
              >
                {paragraph}
              </motion.p>
            ))}
          </motion.div>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="mt-20"
            >
              <h2 className="text-3xl font-bold text-[#1E3D1A] mb-8">
                Articles similaires
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {relatedArticles.map((relatedArticle) => (
                  <Link
                    key={relatedArticle.id}
                    to={`/actualites/${relatedArticle.slug}`}
                    className="group"
                  >
                    <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
                      <div className="relative h-56 overflow-hidden">
                        <img
                          src={relatedArticle.image}
                          alt={relatedArticle.title}
                          className="w-full h-full object-cover object-center transform group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                      </div>
                      <div className="p-6">
                        <h3 className="text-xl font-semibold text-[#1E3D1A] mb-3 line-clamp-2">
                          {relatedArticle.title}
                        </h3>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Calendar className="w-4 h-4" />
                          <span>{parsedDate(relatedArticle.date)}</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </motion.div>
          )}

          {/* Back to Articles */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mt-16 text-center"
          >
            <Link
              to="/actualites"
              className="inline-flex items-center text-[#1E3D1A] hover:text-[#1E3D1A]/80 transition-colors duration-300"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Retour aux articles
            </Link>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ArticleDetails;
